import React from 'react';
import { Clock, PlayCircle, RefreshCw, TrendingDown } from 'lucide-react';
import { formatDuration } from '../../../services/videoEngagementAnalyticsService';

const StatCard = ({ icon: IconComponent, label, value, sub, color, loading }) => {
  const colorMap = {
    purple: 'from-purple-500 to-purple-700',
    blue: 'from-blue-500 to-blue-700',
    amber: 'from-amber-500 to-amber-700',
    rose: 'from-rose-500 to-rose-700',
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${colorMap?.[color]}`}>
          <IconComponent className="w-5 h-5 text-white" />
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 bg-white/20 rounded-lg animate-pulse w-24" />
          <div className="h-4 bg-white/10 rounded-lg animate-pulse w-32" />
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold text-white mb-1">{value}</p>
          <p className="text-purple-200 text-sm font-medium">{label}</p>
          {sub && <p className="text-purple-400 text-xs mt-1">{sub}</p>}
        </>
      )}
    </div>
  );
};

const SummaryStatCards = ({ stats, levelData, loading }) => {
  return (
    <div className="space-y-5">
      {/* 4 main metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Clock}
          label="Avg Watch Time"
          value={formatDuration(stats?.avgWatchSeconds)}
          sub={`${stats?.totalWatchMinutes?.toLocaleString() || 0} total minutes watched`}
          color="purple"
          loading={loading}
        />
        <StatCard
          icon={PlayCircle}
          label="Total Watch Events"
          value={(stats?.totalEvents || 0)?.toLocaleString()}
          sub={`${stats?.activeLearners || 0} unique learners`}
          color="blue"
          loading={loading}
        />
        <StatCard
          icon={RefreshCw}
          label="Replay Rate"
          value={`${stats?.replayRate || 0}%`}
          sub={`${(stats?.totalReplays || 0)?.toLocaleString()} total replays`}
          color="amber"
          loading={loading}
        />
        <StatCard
          icon={TrendingDown}
          label="Avg Drop-off Point"
          value={`${stats?.avgDropOffPct || 0}%`}
          sub="Where non-completers stop watching"
          color="rose"
          loading={loading}
        />
      </div>

      {/* Per-level engagement metrics strip */}
      {!loading && levelData?.length > 0 && (
        <div className="bg-white/5 rounded-2xl border border-white/10 p-4">
          <p className="text-purple-300 text-xs font-semibold uppercase tracking-wide mb-3">Drop-off Point by Level</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {levelData?.map(level => (
              <div key={level?.level} className="bg-white/5 rounded-xl p-3 text-center">
                <p className="text-purple-300 text-xs font-medium mb-1">{level?.label}</p>
                <p className="text-white font-bold text-sm">
                  {level?.dropOffPct !== null ? `${level?.dropOffPct}%` : '—'}
                </p>
                <p className="text-purple-500 text-xs mt-0.5">
                  {level?.totalViews > 0 ? `${level?.totalViews} views` : 'No data'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryStatCards;
